import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Star, CheckCircle2, AlertCircle, Send } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Label } from "../components/ui/label";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FeedbackPage = () => {
  const { orderId } = useParams();
  const [params] = useSearchParams();
  const email = params.get("email") || "";
  const token = params.get("token") || "";
  const presetRating = parseInt(params.get("rating")) || null;

  const [rating, setRating] = useState(presetRating);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [courseTitle, setCourseTitle] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/feedback/check`, {
          params: { order_id: orderId, email, token },
        });
        setCourseTitle(res.data.course_title);
        if (res.data.previous_rating) {
          setRating(res.data.previous_rating);
        }
      } catch (err) {
        setError(err.response?.data?.detail || "Invalid feedback link");
      } finally {
        setLoading(false);
      }
    })();
  }, [orderId, email, token]);

  const submit = async () => {
    if (!rating) {
      setError("Please choose a rating before submitting.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await axios.post(`${API}/feedback`, {
        order_id: orderId,
        email,
        token,
        rating,
        comment: comment.trim() || null,
      });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not submit feedback");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 p-8 flex items-center justify-center">
        <div className="animate-pulse w-full max-w-xl">
          <div className="h-64 skeleton rounded-xl" />
        </div>
      </div>
    );
  }
  if (error && !courseTitle) {
    return (
      <div className="min-h-screen bg-slate-50 p-8 flex items-center justify-center">
        <Card className="w-full max-w-lg">
          <CardContent className="p-10 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
            <h2 className="font-heading font-bold text-xl mb-2">Link not valid</h2>
            <p className="text-sm text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }
  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 p-8 flex items-center justify-center">
        <Card className="w-full max-w-lg" data-testid="feedback-success">
          <CardContent className="p-10 text-center">
            <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="font-heading font-bold text-2xl mb-2">Thank you!</h2>
            <p className="text-muted-foreground">
              Your feedback for <strong>{courseTitle}</strong> has been recorded. We really appreciate it.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-8 flex items-center justify-center">
      <Card className="w-full max-w-xl" data-testid="feedback-form">
        <CardContent className="p-8">
          <h1 className="font-heading font-bold text-2xl text-primary mb-1">
            Share your feedback
          </h1>
          <p className="text-sm text-muted-foreground mb-6">
            How was your registration experience for <strong>{courseTitle}</strong>?
          </p>

          {/* Star rating */}
          <div className="flex items-center justify-center gap-2 mb-6" data-testid="star-rating">
            {[1, 2, 3, 4, 5].map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setRating(r)}
                onMouseEnter={() => setHover(r)}
                onMouseLeave={() => setHover(0)}
                className="p-1 transition-transform hover:scale-110"
                aria-label={`${r} stars`}
                data-testid={`star-${r}`}
              >
                <Star
                  className={`w-10 h-10 transition-colors ${
                    (hover || rating) >= r ? "fill-amber-400 text-amber-400" : "text-slate-300"
                  }`}
                />
              </button>
            ))}
          </div>
          {rating && (
            <p className="text-center text-sm text-muted-foreground mb-6">
              {rating === 5 && "Excellent! Thanks for the love."}
              {rating === 4 && "Great — thanks for the positive feedback."}
              {rating === 3 && "Fair. Tell us what we can do better."}
              {rating === 2 && "We can do better. What went wrong?"}
              {rating === 1 && "We're sorry. Please tell us what happened so we can fix it."}
            </p>
          )}

          {/* Optional comment */}
          <div className="space-y-2 mb-6">
            <Label htmlFor="comment">Anything you'd like us to know? <span className="text-muted-foreground">(optional)</span></Label>
            <Textarea
              id="comment"
              rows={4}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="What worked well? What could be better?"
              data-testid="feedback-comment"
            />
          </div>

          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Button
            onClick={submit}
            disabled={submitting || !rating}
            className="w-full bg-brand hover:bg-brand-dark text-white"
            data-testid="feedback-submit-btn"
          >
            <Send className="h-4 w-4 mr-2" />
            {submitting ? "Submitting..." : "Submit feedback"}
          </Button>
          <p className="text-xs text-muted-foreground text-center mt-4">
            Your response is anonymous to other students. Only our team sees it.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default FeedbackPage;
